import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, Handshake } from 'lucide-react';
import api from '../api';
import EventCard from '../components/EventCard';
import BannerCarousel from '../components/BannerCarousel';
import SEO from '../components/SEO';

export default function HomePage() {
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasBanners, setHasBanners] = useState(false);

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            const events = await api.getUpcomingEvents(6);
            setUpcomingEvents(events);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    };

    const features = [
        {
            icon: Calendar,
            title: 'Centralized Calendar',
            description: 'We aggregate performances, workshops, and cultural gatherings from partner organizations into one comprehensive, easy-to-navigate calendar system.'
        },
        {
            icon: Users,
            title: 'Community Connection',
            description: 'We connect audiences with diverse cultural experiences, fostering a stronger, more engaged arts community across Singapore.'
        },
        {
            icon: Handshake,
            title: 'Partner Empowerment',
            description: 'We provide our partner organizations with greater visibility and reach, helping them connect with wider audiences.'
        }
    ];

    return (
        <main className="main">
            <SEO 
                title="Home"
                description="Discover Singapore's Indian Heritage and Arts events, workshops, and performances. A unified platform bringing together cultural experiences celebrating Indian and multicultural traditions."
                keywords="Singapore Indian events, Indian Heritage events, Indian heritage Singapore, arts events, cultural workshops, Indian performances, ATAM Connect"
            />
            {/* Banner Carousel */}
            <BannerCarousel onBannersLoaded={(exists) => setHasBanners(exists)} />



            {/* Hero Section - Vibrant Gradient with Video Background */}
            {!hasBanners && (
                <section className="hero" style={{
                    position: 'relative',
                    paddingTop: '120px',
                    paddingBottom: '100px',
                    overflow: 'hidden',
                    minHeight: '600px',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    {/* Video Background */}
                    <iframe
                        src="https://www.youtube.com/embed/9-H5g3oew1I?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playlist=9-H5g3oew1I&playsinline=1"
                        title="Background Video"
                        allow="autoplay; encrypted-media"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '100vw',
                            height: '56.25vw', // 16:9 aspect ratio
                            minWidth: '177.77vh', // 16:9 aspect ratio
                            minHeight: '100vh',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                            zIndex: 0,
                            border: 'none'
                        }}
                    />

                    {/* Gradient Overlay - Reduced opacity to show video */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, rgba(222, 65, 176, 0.9) 0%, rgba(254, 205, 109, 0.9) 50%, rgba(255, 252, 242, 0.9) 100%)',
                        zIndex: 1,

                    }} />

                    {/* Content */}
                    <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                        <h1 style={{
                            maxWidth: '900px',
                            margin: '0 auto 32px',
                            textAlign: 'center',
                            animation: 'fadeInUp 0.8s ease-out',
                            color: '#000000',
                            textShadow: '0 2px 10px rgba(255,255,255,0.3)'
                        }}>
                            <em className="serif-accent">Discover</em>
                            {' '}
                            <span>Singapore's Indian Heritage and Arts</span>
                        </h1>
                        <p style={{
                            fontSize: '20px',
                            maxWidth: '620px',
                            lineHeight: '1.5',
                            fontWeight: '400',
                            animation: 'fadeInUp 0.8s ease-out 0.2s',
                            animationFillMode: 'both',
                            color: '#212121',
                            textShadow: '0 1px 5px rgba(255,255,255,0.5)'
                        }}>
                            A unified platform that brings together arts, culture and heritage events, workshops
                            and performances across Singapore
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '16px',
                            justifyContent: 'center',
                            marginTop: '48px',
                            flexWrap: 'wrap',
                            animation: 'fadeInUp 0.8s ease-out 0.4s',
                            animationFillMode: 'both'
                        }}>
                            <Link to="/events" className="btn btn-primary">
                                Browse Events
                            </Link>
                            <Link to="/calendar" className="btn btn-secondary">
                                <Calendar size={20} /> View Calendar
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* What We Do - Soft Cards */}
            <section style={{ padding: '100px 0', background: '#FAF8F5' }}>
                <div className="container">
                    <h2 style={{
                        fontSize: '48px',
                        fontWeight: '700',
                        textAlign: 'center',
                        marginBottom: '64px'
                    }}>
                        What We Do
                    </h2>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '24px'
                    }}>
                        {features.map((feature) => (
                            <div
                                key={feature.title}
                                style={{
                                    background: 'white',
                                    borderRadius: '24px',
                                    padding: '36px',
                                    border: '1px solid rgba(0, 0, 0, 0.06)',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                                    transition: 'all 0.3s'
                                }}
                            >
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '16px',
                                    background: '#F0F4F8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '20px'
                                }}>
                                    <feature.icon size={28} strokeWidth={1.5} />
                                </div>
                                <h3 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '12px' }}>
                                    {feature.title}
                                </h3>
                                <p style={{ color: '#4B5563', fontSize: '16px', lineHeight: '1.7' }}>
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Upcoming Events */}
            <section style={{ padding: '100px 0', background: '#FFFFFF' }}>
                <div className="container">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '48px'
                    }}>
                        <h2 style={{
                            fontSize: '48px',
                            fontWeight: '700'
                        }}>
                            Upcoming Events
                        </h2>
                        <Link to="/events" className="btn btn-ghost" style={{ fontSize: '15px' }}>
                            View All <ArrowRight size={16} />
                        </Link>
                    </div>

                    {loading ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="skeleton" style={{ height: '400px', borderRadius: '24px' }} />
                            ))}
                        </div>
                    ) : upcomingEvents.length > 0 ? (
                        <div className="events-grid">
                            {upcomingEvents.map((event) => (
                                <EventCard key={event.id} event={event} />
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '80px 24px',
                            color: '#9CA3AF',
                            background: '#F9FAFB',
                            borderRadius: '28px'
                        }}>
                            <Calendar size={64} style={{ marginBottom: '24px', opacity: 0.3 }} />
                            <h3 style={{ fontSize: '24px', marginBottom: '8px', color: '#000000' }}>
                                No upcoming events yet
                            </h3>
                            <p>Check back soon for new events celebrating Singapore's Indian Heritage and Arts!</p>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA - Soft Gradient */}
            <section style={{
                padding: '100px 0',
                background: 'linear-gradient(135deg, #F0F4F8 0%, #FAF8F5 100%)',
                margin: '0 24px 80px',
                borderRadius: '32px'
            }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h2 style={{
                        fontSize: 'clamp(36px, 6vw, 56px)',
                        fontWeight: '700',
                        marginBottom: '20px',
                        letterSpacing: '-0.02em',
                        lineHeight: '1.1'
                    }}>
                        <em className="serif-accent">Experience</em>
                        {' the Movement'}
                    </h2>
                    <p style={{
                        color: '#4B5563',
                        marginBottom: '40px',
                        fontSize: '18px',
                        maxWidth: '600px',
                        margin: '0 auto 40px'
                    }}>
                        Join our vibrant community and discover Singapore's Indian Heritage and Arts events
                    </p>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link to="/events" className="btn btn-primary">
                            Browse Events
                        </Link>
                        <Link to="/contact" className="btn btn-secondary">
                            Partner With Us
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
